// ─────────────────────────────────────────────────────────────────────────────
// utils/umgebung.ts — DIE EINE STELLE, an der die Oberflaeche entscheidet, ob
// sie im Produktivsystem laeuft oder nicht (T-M32.7, REQ-OPS-061).
//
// ── DIE BILDUNGSREGEL, und warum sie an der PROJEKT-ID haengt ───────────────
// Die zwei Umgebungen unterscheiden sich in GENAU EINEM Parameter: der
// Firebase-Projekt-ID (REQ-OPS-061). Alles Uebrige ist daraus abgeleitet. Das
// Kennzeichen der Oberflaeche liest deshalb denselben einen Parameter und
// keinen zweiten, eigens gefuehrten Schalter — ein `VITE_IST_TESTSYSTEM` waere
// ein zweiter gefuehrter Ort (REQ-OPS-038) und koennte der Projekt-ID
// widersprechen, ohne dass es jemandem auffiele.
//
// ── DER ZWEITE TEIL DER REGEL, ausdruecklich BEGRUENDET ─────────────────────
// Zur reinen Projekt-ID kommt EIN weiterer Fall: `VITE_USE_EMULATORS === 'true'`.
// Der Grund ist ein gemessener Zustand des Bestandes und keine Vorliebe:
// `dashboard/.env.local` traegt `VITE_FIREBASE_PROJECT_ID=printerque-prod`
// (nachgemessen am 2026-09-04). Wer lokal gegen den Emulator entwickelt, setzt
// dort `VITE_USE_EMULATORS=true` und AENDERT DIE PROJEKT-ID NICHT — sie ist bei
// einem Emulatorlauf ohnehin nur die Audience der ID-Token (REQ-OPS-064 (a)).
// Ohne diesen zweiten Fall zeigte die Oberflaeche am Emulator „Produktion" an,
// obwohl kein einziges Datum aus der Produktion stammt. Das ist genau die
// Verwechslung, gegen die Kriterium 4 von T-M32.7 gebaut ist, nur in der
// harmlosen Richtung — und ein Kennzeichen, das in einem offensichtlichen Fall
// schweigt, verliert seine Glaubwuerdigkeit auch im gefaehrlichen.
//
// ⚠ ES IST TROTZDEM KEIN ZWEITER GEFUEHRTER ORT: `VITE_USE_EMULATORS` besteht
// seit jeher, wird in `src/firebase/config.ts` mit demselben Vergleich gelesen
// (`=== 'true'`) und beschreibt eine ANDERE Tatsache als die Projekt-ID (wohin
// die Verbindung geht, nicht welches Projekt gemeint ist). Hier wird er
// gelesen, nicht gefuehrt. Dass beide Stellen denselben Vergleich benutzen,
// haelt `test/umgebungsdateien.test.ts` (Zusicherung 4) — sonst driftete die
// eine gegen die andere, ohne dass etwas rot wuerde.
//
// ── WAS DIESE DATEI NICHT TUT (REQ-OPS-036) ────────────────────────────────
// Sie fasst die Firebase-Anbindung nicht an (ausdruecklich nicht im Umfang von
// T-M32.7) und sie waehlt keine Konfiguration aus. Sie beantwortet EINE Frage:
// „Ist das hier das Produktivsystem?" — und liefert den Text dazu.
// ─────────────────────────────────────────────────────────────────────────────

/** Die kanonische Projekt-ID der Produktion (REQ-OV-006, REQ-OPS-061). */
export const PRODUKTIONS_PROJEKT_ID = 'printerque-prod';

/**
 * Das Praefix des Fenstertitels im Testsystem. Bewusst technisch-englisch und
 * bewusst KEIN i18n-Schluessel: es ist eine Kennzeichnung im Tab-Titel und in
 * der Fensterleiste, die in jeder Sprache dieselbe bleiben soll — dieselbe
 * Abwaegung wie bei den Tastenkappen des Fastline-Tastenfelds.
 */
export const TITEL_PRAEFIX = '[TEST] ';

/** Die Teilmenge von `import.meta.env`, die hier gelesen wird. */
export interface UmgebungsWerte {
  VITE_FIREBASE_PROJECT_ID?: string;
  VITE_USE_EMULATORS?:       string;
}

/**
 * Die Projekt-ID, auf die die geladene Oberflaeche zeigt.
 *
 * Der Vorgabewert wird ERST BEIM AUFRUF ausgewertet, nicht beim Laden des
 * Moduls — nur so wirkt ein `vi.stubEnv` im Beleg, und nur so liest eine
 * Ansicht beim Rendern den geltenden Wert.
 */
export function umgebungsProjektId(env: UmgebungsWerte = import.meta.env): string {
  return (env.VITE_FIREBASE_PROJECT_ID ?? '').trim();
}

/** Laeuft die Oberflaeche gegen den lokalen Emulator? */
export function istEmulatorlauf(env: UmgebungsWerte = import.meta.env): boolean {
  return env.VITE_USE_EMULATORS === 'true';
}

/**
 * DIE FRAGE DIESER DATEI: ist das hier NICHT das Produktivsystem?
 *
 * Wahr, sobald die Projekt-ID von `printerque-prod` abweicht — auch dann, wenn
 * sie ganz fehlt: eine Oberflaeche ohne Projekt-ID ist in keinem Fall die
 * belegte Produktion, und im Zweifel ist das Kennzeichen zu viel besser als zu
 * wenig (dieselbe Haltung wie REQ-OPS-065 (c): laut statt still).
 */
export function istTestumgebung(env: UmgebungsWerte = import.meta.env): boolean {
  return umgebungsProjektId(env) !== PRODUKTIONS_PROJEKT_ID || istEmulatorlauf(env);
}

/**
 * Setzt das Titel-Praefix, wenn die Umgebung nicht die Produktion ist.
 *
 * Mehrfach aufrufbar: das Praefix wird nie doppelt gesetzt. Im Produktionsbau
 * tut die Funktion nichts — der Titel bleibt der aus `index.html`.
 */
export function titelKennzeichenAnwenden(
  doc: { title: string } = document,
  env: UmgebungsWerte = import.meta.env,
): void {
  if (!istTestumgebung(env)) return;
  if (doc.title.startsWith(TITEL_PRAEFIX)) return;
  doc.title = TITEL_PRAEFIX + doc.title;
}
